// API Güvenlik Monitörü (Middleware İzleme, süper admin) kullanım rehberi. Metinler 'guides' çeviri alanında.
import type { TFunction } from 'i18next';
import type { GuideContent } from '../../components/common/UsageGuideDialog';

const G = 'superadmin.middlewareMonitor';

export const middlewareMonitorGuide = (t: TFunction): GuideContent => ({
  layout: 'tabbed',
  title: t(`${G}.title`, 'API Güvenlik Monitörü nasıl kullanılır?'),
  intro: t(`${G}.intro`, 'API servislerine gelen istekleri sınırlayan ve engelleyen kuralları yönetir, güvenlik loglarını ve sunucu log dosyalarını izlersiniz. Kurallar tüm müşterilerin erişimini etkileyebilir; dikkatli kullanın. Haritada bir bölgeye tıklayarak o adıma geçebilirsiniz.'),
  regionLabels: {
    header: t(`${G}.regions.header`, 'Özet kartları ve Yenile'),
    tabs: t(`${G}.regions.tabs`, 'Sekmeler'),
    body: t(`${G}.regions.body`, 'Sekme içeriği'),
  },
  navItems: [
    t(`${G}.nav.rateLimit`, 'Rate Limit Kuralları'),
    t(`${G}.nav.ip`, 'IP Erişim Kuralları'),
    t(`${G}.nav.logs`, 'Güvenlik Logları'),
    t(`${G}.nav.live`, 'Canlı İzleme'),
    t(`${G}.nav.files`, 'Dosya Logları'),
    t(`${G}.nav.jwt`, 'JWT Devreye Alma'),
  ],
  steps: [
    {
      region: 'header',
      title: t(`${G}.s1.title`, '1. Özet kartlarını okuyun'),
      text: t(`${G}.s1.text`, 'Kartlar Rate Limit, IP Engel, Auth Hata ve Exception kayıt sayılarını ve aktif Rate Limit / IP kuralı sayısını gösterir. Kayıt varsa altında son 24 saatte en çok engellenen IP\'ler ve en çok hata veren endpoint\'ler listelenir. "Yenile" tüm verileri yeniden yükler.'),
    },
    {
      region: 'tabs',
      title: t(`${G}.s2.title`, '2. Rate Limit Kuralları: istek sınırı'),
      text: t(`${G}.s2.text`, '"Yeni Kural" ile Kural Adı, Scope (GLOBAL, COMPANY, IP, ENDPOINT), Max İstek, Pencere (sn), isteğe bağlı Burst Limit (saniyedeki üst sınır) ve Öncelik girin. Sınırı aşan istek 429 (çok fazla istek) yanıtı alır. Satırdaki anahtar kuralı açar / kapatır, çöp kutusu siler.'),
      tips: [
        t(`${G}.s2.tip1`, 'Bir isteğe birden çok kural uyuyorsa Öncelik değeri en küçük olan uygulanır.'),
        t(`${G}.s2.tip2`, 'Fazla düşük bir GLOBAL sınır tüm müşterilerin isteklerini keser. Yeni kuralı küçük bir kapsamla deneyin.'),
        t(`${G}.s2.tip3`, 'Servisler kuralları önbellekte tutar; değişikliğin etkisi birkaç dakika gecikebilir.'),
      ],
    },
    {
      region: 'tabs',
      title: t(`${G}.s3.title`, '3. IP Erişim Kuralları: kara ve beyaz liste'),
      text: t(`${G}.s3.text`, '"Yeni Kural" ile IP Adresi (aralık için IP Aralık Sonu), Tip, Scope (GLOBAL veya COMPANY + Şirket ID) ve Sebep girin. Kara Liste: o IP\'den gelen istekler 403 ile reddedilir. Engellenen sütunu kuralın kaç isteği durdurduğunu gösterir.'),
      tips: [
        t(`${G}.s3.tip1`, 'Beyaz Liste dikkat ister: aktif bir beyaz liste kuralı varsa, o kapsamda listede OLMAYAN tüm IP\'ler engellenir. GLOBAL bir beyaz liste kuralı, listedeki IP\'ler dışında herkesin erişimini keser.'),
        t(`${G}.s3.tip2`, 'Kuralı silmek yerine anahtarla pasif yapabilirsiniz. Değişikliğin etkisi önbellek nedeniyle birkaç dakika gecikebilir.'),
      ],
    },
    {
      region: 'body',
      title: t(`${G}.s4.title`, '4. Güvenlik Logları ve Canlı İzleme'),
      text: t(`${G}.s4.text`, 'Güvenlik Logları sekmesinde Log Tipi, API, IP ve Şirket ID ile süzün; "Sadece Hatalar" yalnız Exception kayıtlarını gösterir. Bir satıra tıklayınca StackTrace ve Request Body dahil tüm ayrıntı açılır. Canlı İzleme son 50 logu gösterir ve sekme açıkken 10 saniyede bir kendiliğinden yenilenir.'),
    },
    {
      region: 'body',
      title: t(`${G}.s5.title`, '5. Dosya Logları: sunucu log dosyaları'),
      text: t(`${G}.s5.text`, 'Sunucudaki C:\\APIs\\<api>\\instance_*\\logs\\*.log dosyalarını okur. Tüm uygulama logları (Info, Warning, Error, Debug) bu dosyalardadır; veritabanındaki güvenlik logundan daha ayrıntılıdır. API, Instance ve Dosya seçin, Satır sayısını ve isteğe bağlı Filtre (grep) metnini girin, "Oku" düğmesine basın.'),
      tips: [
        t(`${G}.s5.tip1`, '[ERR] Hatalar, [WRN] Uyarılar ve Exception düğmeleri filtreyi tek tıkla doldurur.'),
        t(`${G}.s5.tip2`, '"Auto-Refresh" açıkken içerik 5 saniyede bir yenilenir. Satır kaydırma, kopyalama ve tam ekran düğmeleri log kutusunun üstündedir.'),
      ],
    },
    {
      region: 'body',
      title: t(`${G}.s6.title`, '6. JWT Devreye Alma: 401 kayıtlarını yorumlayın'),
      text: t(`${G}.s6.text`, 'OwnJwt:Enabled bayrağı açılan servislerde JWT olmadan gelen her istek burada servis ve yol bazında özetlenir. Pencere ile süreyi seçin; "Otomatik" açıkken 30 saniyede bir yenilenir.'),
      tips: [
        t(`${G}.s6.tip1`, 'Her kayıt hata değildir; gerçek yetkisiz erişim denemeleri de aynı tabloya düşer.'),
        t(`${G}.s6.tip2`, 'İstek/IP oranı yüksek ve User-Agent tanıdıksa bu bizim çağrımızdır: muafiyet eksiktir ya da çağıran token göndermiyordur; önce çağıranı bulun.'),
        t(`${G}.s6.tip3`, 'Çok sayıda IP\'ye dağılmış tek tek kayıtlar dış taramadır; bunlara dokunmayın.'),
      ],
    },
  ],
});
